import { isHostile, reachFailure, resolvePlayerAttack } from './attack.js';
import { applyAttack, applyEffect, canAct } from './effects.js';
import { modifierTotal } from './items.js';
import { canAffordMana, spendMana } from './mana.js';
import { atSkillLevel, isSkillUnlocked, skillById, skillCooldownTicks, skillDurationTicks, skillLevel, skillManaCost, skillModifiers, } from './skills.js';
import { DAMAGE_TYPES, applyResistances, createDamage, mapDamageTypes, } from './stats.js';
import { entityById } from './world.js';
/**
 * Whether a character may cast a skill right now — and, once it may, one cast
 * from the roll to the wound.
 *
 * This is to `skills.ts` what `attack.ts` is to `combat.ts`: the tree owns what
 * a skill *is*, and this owns whether it may be used and what happens when it
 * is. Everything it does is composition — nothing here re-derives damage,
 * resistance, death or a duration:
 *
 *   | a skill that…            | is resolved by                                   |
 *   |--------------------------|--------------------------------------------------|
 *   | swings a weapon          | `resolvePlayerAttack` — `resolveAttack` + `applyAttack` |
 *   | throws a bolt            | `applyResistances`, then `applyAttack`           |
 *   | buffs or curses          | `applyEffect` with a `ModifierEffect`            |
 *   | poisons                  | `applyEffect` with a `DamageOverTime`            |
 *
 * A bolt is **not** a swing and must not be resolved as one: D2 spells roll no
 * attack rating and are not blocked, which is why `resolveAttack` is reached
 * only by the weapon-attack rows. What a bolt *does* share with a swing is
 * everything after the roll — the resistance table, the poison split, the death
 * transition — so it builds an `AttackResult` and hands it to `applyAttack`
 * rather than teaching this module how a life pool works.
 *
 * ## The anti-cheat shape of it (`ARCHITECTURE.md`)
 *
 * A cast intent carries a skill id and a target id and nothing else — never a
 * damage number, never a level, never a mana cost. `admitCast` is what a server
 * turns those two ids into a cast with, and every refusal is a thing a client
 * might try:
 *
 *   | refusal              | what the client tried                                |
 *   |----------------------|------------------------------------------------------|
 *   | `unknown-skill`      | a fabricated skill id                                |
 *   | `passive`            | casting something D2 never casts                     |
 *   | `unembodied`         | casting with no body on this floor                   |
 *   | `caster-dead`        | casting while dead                                   |
 *   | `unlearned`          | a skill with no points, or another class's skill      |
 *   | `locked`             | a skill whose level gate or prerequisites are unmet  |
 *   | `cooling`            | casting inside D2's casting delay                    |
 *   | `insufficient-mana`  | casting what it cannot afford                        |
 *   | `no-target`          | naming a body that does not exist, or none at all    |
 *   | `friendly`           | naming itself, or a party member                     |
 *   | `target-dead`        | naming a corpse                                      |
 *   | `out-of-reach`       | naming a body across the floor                       |
 *   | `no-line-of-sight`   | naming a body through a wall                         |
 *
 * The order is `admitSwing`'s and for the same reason: everything about the
 * **caster** first, then the **skill**, then the **target**, then the **space
 * between them**. A dead caster is refused for being dead whatever it named, and
 * the two lookups that cost anything — the reach measurement and the sight trace
 * — are last. The reach rule itself is `attack.ts`'s `reachFailure`, called
 * rather than restated, because "can this body touch that one" is one rule with
 * one home whether a fist or a bolt is crossing the gap.
 *
 * `locked` is checked at the cast and not only at the spend on purpose. A
 * character cannot normally hold a point in a gated skill, but a save restored
 * at a level that disagrees with its own ledger can, and a gate that was only
 * ever enforced on the way in would honour it.
 *
 * ## Determinism, and the roll budget of a cast
 *
 * Rolls are drawn only while they can change the outcome, and the count is a
 * function of the effect kind alone — so a replay consumes the identical slice
 * of the stream:
 *
 *   - `spell-damage` and `damage-over-time`: **one** draw, the damage range.
 *   - `weapon-attack`: `resolveAttack`'s own fixed budget — 1 on a miss, 2 on a
 *     block, 8 on a hit.
 *   - `timed-modifier`: **none**. A buff has no roll.
 *
 * A refused cast draws **nothing at all**, which is why `admitCast` takes no
 * `Rng` and `resolveCast` cannot be reached without one.
 */
const PERCENT = 100;
/**
 * Whether a caster that last cast this skill on `lastCastTick` may cast it again
 * on `tick`.
 *
 * D2's casting delay: "a period of time after a player has cast a spell during
 * which the player cannot re-cast that spell… There is nothing you can do to
 * reduce or eliminate the Casting Delay" (The Arreat Summit, "Casting Delays").
 * Most skills have none, and for those this is true on every tick.
 *
 * `null` — never cast — is ready. A `tick` behind the last cast is not: a clock
 * that went backwards grants no credit, exactly as `readyToSwing` refuses it.
 */
export const readyToCast = (skill, tick, lastCastTick) => lastCastTick === null || tick - lastCastTick >= skillCooldownTicks(skill);
/**
 * Decides whether a character may cast a skill right now.
 *
 * Pure, and it draws nothing: a refused cast costs no roll, so a client
 * hammering the server cannot advance the random stream by asking. See the
 * module header for the order of the checks and what each is protecting
 * against.
 */
export const admitCast = (request) => {
    const skill = skillById(request.skillId, request.catalogue);
    if (skill === undefined)
        return { ok: false, reason: 'unknown-skill' };
    if (skill.effect.kind === 'passive')
        return { ok: false, reason: 'passive' };
    const caster = entityById(request.world, request.casterId);
    if (caster === null)
        return { ok: false, reason: 'unembodied' };
    const casterVitals = request.casterVitals;
    if (!canAct(casterVitals))
        return { ok: false, reason: 'caster-dead' };
    const level = skillLevel(request.progress, skill.id);
    if (level === 0)
        return { ok: false, reason: 'unlearned' };
    if (!isSkillUnlocked(skill, request.progress))
        return { ok: false, reason: 'locked' };
    if (!readyToCast(skill, request.tick, request.lastCastTick))
        return { ok: false, reason: 'cooling' };
    const cost = skillManaCost(skill, level);
    if (!canAffordMana(request.mana, cost))
        return { ok: false, reason: 'insufficient-mana' };
    const admitted = {
        skill,
        level,
        cost,
        caster,
        casterVitals,
        casterStats: request.casterStats,
        mana: request.mana,
    };
    // A self-cast has no target to judge, and ignores one it was handed rather
    // than refusing: a client that sends its current target with every intent is
    // not cheating, it is just verbose.
    if (skill.target !== 'enemy')
        return { ok: true, cast: { ...admitted, target: null } };
    const entity = request.targetId === null ? null : entityById(request.world, request.targetId);
    if (entity === null)
        return { ok: false, reason: 'no-target' };
    if (!isHostile(caster, entity))
        return { ok: false, reason: 'friendly' };
    const vitals = request.targetVitals;
    if (!canAct(vitals))
        return { ok: false, reason: 'target-dead' };
    const stats = request.targetStats;
    // A body with a life but no stat block is a body nothing could measure a
    // resistance against, so it is no more a target than a missing one is.
    if (stats === null)
        return { ok: false, reason: 'no-target' };
    const failure = reachFailure(request.world, caster, entity, skill.rangeCells);
    if (failure !== null)
        return { ok: false, reason: failure };
    return { ok: true, cast: { ...admitted, target: { entity, vitals, stats } } };
};
// ---------------------------------------------------------------------------
// Resolving one cast.
//
// The dispatch below is over the five kinds of *machinery* a skill can be, not
// over the skills themselves — which is the distinction that keeps this file
// from rotting. Nine skills today and ninety later are rows in `skills.ts`'s
// table, and not one of them adds an arm here: a new fire spell is a `rows(...)`
// edit, and only a genuinely new kind of thing — a summon, an aura — is a new
// arm. That is the same shape `effects.ts` uses to tell a promise of damage from
// a buff.
// ---------------------------------------------------------------------------
const NO_DAMAGE = createDamage();
/** Nothing happened to anybody. What a passive lands, and the shell every other landing fills in. */
const quiet = (caster) => ({
    caster,
    target: null,
    attack: null,
    damage: NO_DAMAGE,
    immune: [],
});
/**
 * A resolved damage roll, already mitigated, in the shape a swing lands in.
 *
 * `hitChance` is 100 and `blockChance` 0 because a D2 spell rolls neither: it
 * cannot miss and cannot be blocked. Reporting them rather than leaving them out
 * is what lets `applyAttack` — and every consumer of an `AttackResult` — take a
 * bolt and a sword blow through one path.
 */
const spellResult = (damage, immune) => ({
    outcome: 'hit',
    hitChance: PERCENT,
    blockChance: 0,
    critical: false,
    damage,
    totalDamage: DAMAGE_TYPES.reduce((total, type) => total + damage[type], 0),
    immune,
});
/**
 * One draw, in D2's own inclusive range: the only roll a bolt or a javelin
 * makes, and `rng.int`'s upper bound is exclusive, so the printed maximum is
 * reachable.
 */
const rollDamage = (minimum, maximum, level, rng) => rng.int(atSkillLevel(minimum, level), atSkillLevel(maximum, level) + 1);
/** Mitigates a single-type roll against the defender, reporting the immunity as `applyResistances` does. */
const mitigated = (type, rolled, defender) => applyResistances(mapDamageTypes((entry) => (entry === type ? rolled : 0)), defender.resistances, defender.resistanceCap);
/**
 * A bolt: one roll, the defender's resistance, then the ordinary landing.
 *
 * `applyAttack` is what settles it, so a spell inherits the instant-versus-poison
 * split and the death transition without this module knowing how either works.
 */
const landSpellDamage = (effect, cast, target, rng) => {
    const rolled = rollDamage(effect.minimum, effect.maximum, cast.level, rng);
    const { damage, immune } = mitigated(effect.damageType, rolled, target.stats);
    return {
        caster: cast.casterVitals,
        target: applyAttack(target.vitals, spellResult(damage, immune)),
        attack: null,
        damage,
        immune,
    };
};
/**
 * A promise of damage rather than damage: the whole roll is spread over the
 * skill's duration by `effects.ts`, which is where D2 puts it too — a poison
 * javelin's listed damage is a total "over X seconds".
 *
 * Nothing is lost on the tick it lands, so the `VitalsChange` reports no life
 * lost and no death. An immune body takes no promise at all rather than one
 * worth nothing, which matters: a zero-rate poison from a character *replaces*
 * whatever was already running (`effects.ts`), and an immunity must not become a
 * way to strip a poison somebody else applied.
 */
const landDamageOverTime = (effect, cast, target, rng) => {
    const rolled = rollDamage(effect.minimum, effect.maximum, cast.level, rng);
    const { damage, immune } = mitigated(effect.damageType, rolled, target.stats);
    const owed = damage[effect.damageType];
    return {
        caster: cast.casterVitals,
        target: {
            vitals: owed === 0
                ? target.vitals
                : applyEffect(target.vitals, {
                    kind: 'damage-over-time',
                    type: effect.damageType,
                    remainingDamage: owed,
                    remainingTicks: skillDurationTicks(effect.durationSeconds, cast.level),
                }),
            lifeLost: 0,
            died: false,
        },
        attack: null,
        damage,
        immune,
    };
};
/**
 * A buff on the caster or a curse on the target, as one `ModifierEffect`.
 *
 * `effects.ts` already holds one effect per id and refreshes it in place on a
 * recast, which is D2's own behaviour, so nothing here has to decide what a
 * second Shout means. Whether it lands on the caster or on the target is the
 * row's `target`, not a second kind of effect: a buff and a debuff are the same
 * shape with opposite signs, which is the argument `effects.ts` makes for
 * `ModifierEffect` in the first place.
 */
const landTimedModifier = (effect, cast, target) => {
    const landed = {
        kind: 'modifier',
        id: effect.effectId,
        modifiers: skillModifiers(effect.modifiers, cast.level),
        remainingTicks: skillDurationTicks(effect.durationSeconds, cast.level),
    };
    if (target === null)
        return quiet(applyEffect(cast.casterVitals, landed));
    return {
        ...quiet(cast.casterVitals),
        target: { vitals: applyEffect(target.vitals, landed), lifeLost: 0, died: false },
    };
};
/**
 * The damage types enhanced damage lifts. D2 applies it to the physical
 * component only — the same rule, and the same reason, as `elites.ts`.
 */
const ENHANCED_DAMAGE_TYPES = ['physical'];
const percentOf = (value, percent) => Math.floor((value * percent) / PERCENT);
const skillRange = (base, modifiers, type, enhanced) => {
    const percent = ENHANCED_DAMAGE_TYPES.includes(type) ? PERCENT + enhanced : PERCENT;
    return {
        min: percentOf(base.min + modifierTotal(modifiers, 'damageMin', type), percent),
        max: percentOf(base.max + modifierTotal(modifiers, 'damageMax', type), percent),
    };
};
/**
 * Moves a share of the physical range into the skill's element.
 *
 * D2's "Converts X% Physical Damage To Elemental Damage", applied after the
 * weapon's own damage is settled, so what converts is what the swing would
 * actually have dealt. It is a *move* and not a bonus: the physical range loses
 * exactly what the element gains, which is why a Cold Arrow off a big weapon is
 * not simply a bigger hit.
 */
const converted = (damage, effect, level) => {
    const percent = atSkillLevel(effect.convertedPercent, level);
    const to = effect.convertedTo;
    if (to === null || to === 'physical' || percent === 0)
        return damage;
    const physical = damage.physical;
    const moved = { min: percentOf(physical.min, percent), max: percentOf(physical.max, percent) };
    return {
        ...damage,
        physical: { min: physical.min - moved.min, max: physical.max - moved.max },
        [to]: { min: damage[to].min + moved.min, max: damage[to].max + moved.max },
    };
};
/**
 * The attacker's stat block with the skill folded in, which is the whole of what
 * a weapon-attack skill *is*.
 *
 * Deliberately shaped like `elites.ts`'s `applyElite`: flat contributions are
 * added first, enhanced damage lifts the physical component, and attack rating
 * is scaled. A skill and an elite affix are the same kind of thing happening to
 * the same stat block, and doing it twice two different ways is how two numbers
 * that should agree stop agreeing.
 */
export const skillAttackStats = (stats, effect, level) => {
    const modifiers = skillModifiers(effect.modifiers, level);
    const enhanced = modifierTotal(modifiers, 'enhancedDamagePercent');
    return {
        ...stats,
        attackRating: percentOf(stats.attackRating, PERCENT + atSkillLevel(effect.attackRatingPercent, level)),
        attack: {
            ...stats.attack,
            damage: converted(mapDamageTypes((type) => skillRange(stats.attack.damage[type], modifiers, type, enhanced)), effect, level),
        },
    };
};
/** A swing with the skill folded into the stat block first. `resolvePlayerAttack` does the rest. */
const landWeaponAttack = (effect, cast, target, rng) => {
    const swing = resolvePlayerAttack(skillAttackStats(cast.casterStats, effect, cast.level), target.stats, target.vitals, rng);
    return {
        caster: cast.casterVitals,
        target: swing.change,
        attack: swing.result,
        damage: swing.result.damage,
        immune: swing.result.immune,
    };
};
/**
 * Which machinery lands this cast.
 *
 * The passive arm is first because it is the one thing that lands nothing at
 * all; `admitCast` refuses a passive outright, so only a caller reaching
 * `resolveCast` directly ever takes it. The `target === null` arm after the buff
 * is the other malformed-row guard: a row that names `'self'` with an effect
 * that has nowhere to put itself lands nothing rather than throwing inside a
 * tick.
 */
const land = (cast, rng) => {
    const effect = cast.skill.effect;
    const target = cast.target;
    if (effect.kind === 'passive')
        return quiet(cast.casterVitals);
    if (effect.kind === 'timed-modifier')
        return landTimedModifier(effect, cast, target);
    if (target === null)
        return quiet(cast.casterVitals);
    if (effect.kind === 'spell-damage')
        return landSpellDamage(effect, cast, target, rng);
    if (effect.kind === 'damage-over-time')
        return landDamageOverTime(effect, cast, target, rng);
    return landWeaponAttack(effect, cast, target, rng);
};
/**
 * Resolves an admitted cast: pays the mana, then lands whatever the skill does.
 *
 * Takes an `AdmittedCast` rather than a request, which is what makes "a refused
 * cast draws nothing" a property of the types instead of a rule a caller has to
 * remember: there is no way to reach a roll without having passed every check.
 *
 * The pool is spent through `mana.ts` rather than subtracted here, so a cost a
 * pool cannot cover leaves the pool exactly as it was instead of driving it
 * negative — `admitCast` has already refused that case, and this is the
 * narrowing that makes it true anyway.
 */
export const resolveCast = (admitted, rng) => {
    const paid = spendMana(admitted.mana, admitted.cost);
    const mana = paid.ok ? paid.mana : admitted.mana;
    return {
        skill: admitted.skill,
        level: admitted.level,
        manaSpent: paid.ok ? paid.spent : 0,
        mana,
        ...land(admitted, rng),
    };
};
/**
 * One cast, judged and resolved in a single call.
 *
 * The entry point a room wants: it cannot admit a cast and then forget to check
 * it, and it cannot roll one it refused. `admitCast` and `resolveCast` stay
 * separate for the caller that needs the decision without the roll — a client
 * greying out a skill button it knows the server would refuse.
 */
export const castSkill = (request, rng) => {
    const decision = admitCast(request);
    return decision.ok ? { ok: true, cast: resolveCast(decision.cast, rng) } : decision;
};
//# sourceMappingURL=casting.js.map